import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { PhasrRecommendationSideMenuWebComponentSchema } from '../types';
import { GzLabeledContentWebComponent } from '../../../ui/gzLabeledContent';
import { translateFn } from '../../../utils/translator.helper';
import { FontSizes, FontWeights, TemplateState } from '../../../types/common/types';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { WEB_COMPONENT_GZ_TITLE } from '../../../webcomponents.constants';
import { GzTextWebComponent } from '../../../ui/gzText';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { PhasrRenderIconSet } from '../../../phasrRecommendations/uiUtils/iconSet';
import { ButtonColor, ButtonSize } from '../../../ui/gzIconButton/types';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import {
  ActionTakenTypes,
  getPhasrActionTakenTypesName,
} from '../../../types/common/phasrTypes';
import { PhasrIcons } from '../../../phasrRecommendations/uiUtils/types';
import { html, render } from 'lit';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { dateFormatter } from '@nirvana/shared/time';
import { OPEN_BEHAVIORAL_PROFILES_PANEL } from '../../../phasrRecommendations/phasrRecommendationsGrid/types';
import { RecommendationType } from "../../../phasrRecommendations/phasrRecommendationsGrid/types";
import { getTimezone } from '../../../utils/global.utils';
import { ColumnKeys as ColumnKeysMonitoredRules } from '../../../phasrMonitoredRules/phasrMonitoredRulesGrid/types';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { NavigationHelper } from '@nirvana/shared/navigation';

export class PhasrRecommendationSideMenuView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $menuTitle: GzTitleWebComponent;
    $generalTitle: GzTextWebComponent;
    $targetedActivityType: GzLabeledContentWebComponent;
    $attackSurfaceReduction: GzLabeledContentWebComponent;
    $monitoredRule: GzLabeledContentWebComponent;
    $createdOn: GzLabeledContentWebComponent;
    $behavioralProfiles: GzLabeledContentWebComponent;
    $actionTaken: GzLabeledContentWebComponent;
    $detailsTitle: GzTextWebComponent;
    $detailsDescription: GzTextWebComponent;
    $mitigationTitle: GzTextWebComponent;
    $mitigationText: GzTextWebComponent;
    $behavioralProfilesPanel: GzSideMenuWebComponent;

    $allowAccessButton: GzButtonWebComponent;
    $restrictAccessButton: GzButtonWebComponent;
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$menuTitle = this.shadow.querySelector(WEB_COMPONENT_GZ_TITLE);
    this.dom.$generalTitle = this.shadow.querySelector('#general-title');
    this.dom.$targetedActivityType = this.shadow.querySelector('#targeted-activity-type');
    this.dom.$attackSurfaceReduction = this.shadow.querySelector('#attack-surface-reduction');
    this.dom.$monitoredRule = this.shadow.querySelector('#monitored-rule');
    this.dom.$createdOn = this.shadow.querySelector('#created-on');
    this.dom.$behavioralProfiles = this.shadow.querySelector('#behavioral-profiles');
    this.dom.$actionTaken = this.shadow.querySelector('#action-taken');
    this.dom.$detailsTitle = this.shadow.querySelector('#details-title');
    this.dom.$detailsDescription = this.shadow.querySelector('#details-description');
    this.dom.$mitigationTitle = this.shadow.querySelector('#mitigation-title');
    this.dom.$mitigationText = this.shadow.querySelector('#mitigation-text');
    this.dom.$behavioralProfilesPanel = this.shadow.querySelector('#behavioralProfilesPanel');
    this.dom.$allowAccessButton = this.shadow.querySelector('#apply-button');
    this.dom.$restrictAccessButton = this.shadow.querySelector('#restrict-access');

    this.setLoadingState();
    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$generalTitle.innerText = translateFn('General');
    this.dom.$targetedActivityType.label = translateFn('Targeted activity type');
    this.dom.$targetedActivityType.hasTooltip = false;
    this.dom.$attackSurfaceReduction.label = translateFn('Attack surface reduction');
    this.dom.$attackSurfaceReduction.hasEllipsis = false;
    this.dom.$attackSurfaceReduction.hasTooltip = false;
    this.dom.$monitoredRule.label = translateFn('Monitored rule');
    this.dom.$monitoredRule.hasTooltip = false;
    this.dom.$createdOn.label = translateFn('Created on');
    this.dom.$createdOn.hasTooltip = false;
    this.dom.$behavioralProfiles.label = translateFn('Behavioral profiles');
    this.dom.$behavioralProfiles.hasTooltip = false;
    this.dom.$actionTaken.label = translateFn('Action taken');
    this.dom.$actionTaken.hasTooltip = false;
    this.dom.$detailsTitle.innerText = translateFn('Details');
    this.dom.$mitigationTitle.innerText = translateFn('Recommendation Mitigation');
    this.dom.$allowAccessButton.innerText = translateFn('Allow access');
    this.dom.$restrictAccessButton.innerText = translateFn('Restrict access');
  }

  buildSuccessView(data: PhasrRecommendationSideMenuWebComponentSchema) {
    this.displayMenuAction(data);
    this.populateFields(data);
    this.removeLoadingState();
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  private displayMenuAction(data: PhasrRecommendationSideMenuWebComponentSchema): void {
    this.dom.$allowAccessButton.disabled = data.type !== RecommendationType.ALLOW_ACCESS
      || data.actionTaken !== ActionTakenTypes.ACTION_NEEDED || !data.phasrUsable;
    this.dom.$restrictAccessButton.disabled = data.type !== RecommendationType.RESTRICT_ACCESS
      || data.actionTaken !== ActionTakenTypes.ACTION_NEEDED || !data.phasrUsable;
    toggleVisibility(this.dom.$allowAccessButton, data.type === RecommendationType.ALLOW_ACCESS);
    toggleVisibility(this.dom.$restrictAccessButton, data.type === RecommendationType.RESTRICT_ACCESS);
  }

  private populateFields(data: PhasrRecommendationSideMenuWebComponentSchema): void {
    const createdOn = dateFormatter(data.createdOn * 1000, getTimezone(), 'DD MMM YYYY, HH:mm');
    const actionTaken = getPhasrActionTakenTypesName().find(action => action.value === data.actionTaken);

    const reductionPotential = data.attackSurfaceReduction  ?? 0;
    let percentageText = `${reductionPotential}%`;
    if (reductionPotential !== 0) {
      percentageText = `-${percentageText}`;
    }
    const attackSurfaceReduction = data.type === RecommendationType.RESTRICT_ACCESS ? percentageText : '-';
    const attackSurfaceReductionStyle = data.type === RecommendationType.RESTRICT_ACCESS ? 'label-percentage' : '';

    this.dom.$menuTitle.innerText = data.name;
    this.dom.$detailsDescription.innerText = data.description;
    this.dom.$createdOn.innerHTML = `<gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.MEDIUM}">${createdOn}</gz-text>`;
    this.dom.$actionTaken.innerHTML = `<gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${
      FontWeights.MEDIUM
    }">${
      actionTaken?.label ??
      getPhasrActionTakenTypesName().find(action => action.value === ActionTakenTypes.ACTION_NEEDED)?.label
    }</gz-text>`;
    this.dom.$mitigationText.innerText = data.mitigation;
    this.dom.$targetedActivityType.innerHTML = `<gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.MEDIUM}">${data.targetedActivityType}</gz-text>`;

    render(html`
      <gz-tooltip style='max-width: 100%;' tooltipText='${data.rule}' isEllipsis>
        <gz-link type='quiet' color='primary' @click='${() => this.handleRuleNameClick(data)}'>
          ${data.rule}
        </gz-link>
      </gz-tooltip>
    `, this.dom.$monitoredRule)

    this.dom.$attackSurfaceReduction.innerHTML = `<gz-text class="${attackSurfaceReductionStyle}" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.MEDIUM}">${attackSurfaceReduction}</gz-text>`;
    render(html`<div class="d-flex align-items-center">
      <gz-button
        @click='${() => {
          this.dom.$behavioralProfiles.dispatchEvent(new CustomEvent(OPEN_BEHAVIORAL_PROFILES_PANEL))
        }}'
        id='behavioral-profiles-button'
        type='quiet'
        color=''
        buttonheight='medium'
        buttonfontsize='small'
        part='button'
      >
          ${PhasrRenderIconSet.createIcon(PhasrIcons.BEHAVIORAL_PROFILES_ICON, ButtonColor.SECONDARY, ButtonSize.SIZE_24, 'svg-icon')}
          <gz-text style="padding-left: 3px" fontsize="${FontSizes.PARAGRAPH_12}" class="icon-value">${data.behavioralProfiles}</gz-text>
        </gz-button>
      </div>`, this.dom.$behavioralProfiles);
  }

  private handleRuleNameClick(data: PhasrRecommendationSideMenuWebComponentSchema): void {
    const params = {
      filters: [
        {
          [ColumnKeysMonitoredRules.COMPANY]: {
            exactMatch: data.companyId,
          },
        },
        {
          [ColumnKeysMonitoredRules.RULE_TRIGGER_DATE]: {
            timeRange: {
              kind: TimeRangeKind.ALL_TO_DATE,
            },
          }
        },
        {
          [ColumnKeysMonitoredRules.RULE_NAME]: {
            complexEnumFilter: [
              {
                value: data.ruleId,
                label: Application.Main.getPhasrDictionary()[data.ruleId]?.['rule_name'] ?? 'N/A',
              }
            ]
          }
        }
      ],
      openSideMenuForFirstItem: true,
    };

    NavigationHelper.navigateTo('/phasr-monitored-rules', params);
  }
}
